const BOOKING_STATUS: Record<string, string> = {
  HELD: 'Резерв времени',
  DRAFT: 'Черновик',
  CONFIRMED: 'Ожидает осмотра',
  EXPIRED: 'Резерв истёк',
  CANCELLED: 'Отменена',
};

const CASE_STATUS: Record<string, string> = {
  READY_FOR_INSPECTION: 'Ожидает осмотра',
  IN_PROGRESS: 'Осмотр начат',
  COMPLETED: 'Завершён',
  CANCELLED: 'Отменён',
};

export function nativeBookingStatusLabel(booking: {
  readonly status: string;
  readonly case_status: string | null;
}): string {
  if (booking.status === 'CONFIRMED' && booking.case_status !== null) {
    return CASE_STATUS[booking.case_status] ?? booking.case_status;
  }
  return BOOKING_STATUS[booking.status] ?? booking.status;
}

export function nativeBookingStatusTone(booking: {
  readonly status: string;
  readonly case_status: string | null;
}): 'success' | 'warning' | 'muted' | 'danger' {
  if (booking.status === 'CANCELLED' || booking.status === 'EXPIRED') {
    return 'danger';
  }
  if (booking.case_status === 'IN_PROGRESS') {
    return 'warning';
  }
  if (booking.status === 'CONFIRMED') {
    return 'success';
  }
  return 'muted';
}

export function formatEkbDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone: 'Asia/Yekaterinburg',
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatHoldCountdown(expiresAt: string | null, nowMs: number): string {
  if (expiresAt === null) {
    return '—';
  }
  const left = Math.max(0, new Date(expiresAt).getTime() - nowMs);
  const totalSec = Math.floor(left / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function ymdInEkb(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Yekaterinburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  const d = parts.find((p) => p.type === 'day')?.value ?? '01';
  return `${y}-${m}-${d}`;
}

export function slotLocalTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone: 'Asia/Yekaterinburg',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;

export function monthKeyFromYmd(ymd: string): string {
  return ymd.slice(0, 7);
}

/** Shift a YYYY-MM month key by delta months (UTC noon anchor, EKB calendar labels). */
export function addMonthsToMonthKey(monthKey: string, delta: number): string {
  const [yRaw, mRaw] = monthKey.split('-');
  const y = Number(yRaw);
  const m = Number(mRaw);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1, 12));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export type MonthDayCell = {
  readonly ymd: string;
  readonly inMonth: boolean;
  readonly dayNum: number;
};

/** 42-cell Monday-first month grid matching approved v0.3 monthCal(). */
export function buildMonthDayCells(monthKey: string): readonly MonthDayCell[] {
  const [yRaw, mRaw] = monthKey.split('-');
  const y = Number(yRaw);
  const m = Number(mRaw);
  const first = new Date(Date.UTC(y, m - 1, 1, 12));
  const startDelta = (first.getUTCDay() + 6) % 7;
  const cells: MonthDayCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(first.getTime() + (i - startDelta) * 86_400_000);
    const ymd = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    cells.push({
      ymd,
      inMonth: ymd.slice(0, 7) === monthKey,
      dayNum: d.getUTCDate(),
    });
  }
  return cells;
}

export function formatMonthLabelRu(monthKey: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone: 'UTC',
      month: 'long',
      year: 'numeric',
    }).format(new Date(`${monthKey}-01T12:00:00.000Z`));
  } catch {
    return monthKey;
  }
}

export function formatDayLongRu(ymd: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone: 'UTC',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(new Date(`${ymd}T12:00:00.000Z`));
  } catch {
    return ymd;
  }
}

export function minuteToTimeLabel(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function timeLabelToMinute(label: string): number {
  const parts = label.split(':').map((x) => Number(x));
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  return h * 60 + m;
}
